import React, { useState } from "react";
import PropTypes from "prop-types";
import "./DeactivatePatient.css";
import "react-toastify/dist/ReactToastify.css";
import { deactivatePatient, reactivatePatient } from "../../../../../service-layer/PatientAdminService";
import LogoutModal from "../../../../components/logout-moal/LogoutModal";
import ConfirmationModal from "./ConfirmationModal";
import { useTranslation } from "react-i18next";
import { CButton } from "@coreui/react";
import { toast } from "react-toastify";

const DeactivatePatient = ({ userId, isActive, patientName, onStatusChange }) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
  const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);
  const [action, setAction] = useState(""); // "deactivate" or "reactivate"

  const handleButtonClick = (actionType) => {
    setAction(actionType);
    setShowFirstConfirmation(true);
  };

  const handleFirstConfirmation = () => {
    setShowFirstConfirmation(false);
    setShowSecondConfirmation(true);
  };

  const handleSecondConfirmation = async () => {
    setShowSecondConfirmation(false);
    setIsProcessing(true);

    try {
      let response;
      if (action === "deactivate") {
        response = await deactivatePatient(userId);
      } else {
        response = await reactivatePatient(userId);
      }

      if (response && response.status === 200) {
        toast.success(
          action === "deactivate" 
            ? t("patientDeactivatedSuccessfully") 
            : t("patientReactivatedSuccessfully")
        );
        // Refresh the parent table
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        toast.error(t("operationFailed"));
      }
    } catch (error) {
      console.error(`Error ${action}ing patient:`, error);
      toast.error(t("operationFailed"));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setShowFirstConfirmation(false);
    setShowSecondConfirmation(false);
    setAction("");
  };

  const getButtonText = () => {
    if (isProcessing) {
      return t("processing");
    }
    return isActive ? t("deactivate") : t("reactivate");
  };

  const getButtonClass = () => {
    if (isProcessing) {
      return "deactivate-patient-button-processing";
    }
    return isActive 
      ? "deactivate-patient-button-active" 
      : "reactivate-patient-button-inactive";
  };

  return (
    <div className="deactivate-patient-button-container">
      <CButton
        className={getButtonClass()}
        onClick={() => handleButtonClick(isActive ? "deactivate" : "reactivate")}
        disabled={isProcessing}
      >
        {getButtonText()}
      </CButton>

      {/* First Confirmation Modal */}
      <ConfirmationModal
        isOpen={showFirstConfirmation}
        onConfirm={handleFirstConfirmation}
        onCancel={handleCancel}
        title={action === "deactivate" ? t("deactivatePatientTitle") : t("reactivatePatientTitle")}
        message={action === "deactivate" 
          ? t("deactivatePatientFirstConfirmation", { name: patientName })
          : t("reactivatePatientFirstConfirmation", { name: patientName })
        }
        confirmButtonText={t("continue")}
        cancelButtonText={t("cancel")}
        isFirstConfirmation={true}
      />

      {/* Second Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSecondConfirmation}
        onConfirm={handleSecondConfirmation}
        onCancel={handleCancel}
        title={action === "deactivate" ? t("finalConfirmation") : t("finalConfirmation")}
        message={action === "deactivate" 
          ? t("deactivatePatientSecondConfirmation", { name: patientName })
          : t("reactivatePatientSecondConfirmation", { name: patientName })
        }
        confirmButtonText={action === "deactivate" ? t("deactivate") : t("reactivate")}
        cancelButtonText={t("cancel")}
        isFirstConfirmation={false}
        isDangerous={action === "deactivate"}
      />
    </div>
  );
};

DeactivatePatient.propTypes = {
  userId: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  patientName: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func,
};

export default DeactivatePatient;